import React, { useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import {
  Archive,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  ChevronRight,
  DotsVertical,
  Edit01,
  Menu,
  Table,
  Tag,
  CellText,
} from "@opendate-app/setlist";
import { toastError } from "../../shared/toastHelper";
import DetailPageLayout, { DetailColumns } from "../ClassesShared/DetailPageLayout";
import ArchiveConfirmModal from "../ClassesShared/ArchiveConfirmModal";
import initialsFor from "../ClassesShared/initials";
import personColorProps from "../ClassesShared/personColor";
import { formatMonthYear, sessionTimeRange } from "../ClassesShared/classLabels";
import DetailField from "../ClassInstances/registrationPage/DetailField";
import "./instructors.css";
import "../ClassesShared/classesShared.css";

const sessionDay = (value, timeZone) => new Date(value).toLocaleDateString("en-US", {
  weekday: "short", month: "short", day: "numeric", timeZone: timeZone || undefined,
});

export function instructorMeta(instructor, allTimeSessionsCount) {
  return [
    instructor.title,
    instructor.email,
    instructor.created_at ? `Added ${formatMonthYear(instructor.created_at)}` : null,
    `${allTimeSessionsCount} ${allTimeSessionsCount === 1 ? "session" : "sessions"} all-time`,
  ].filter(Boolean).join(" · ");
}

export function upcomingSummary(sessionsCount, classesCount, shown) {
  if (sessionsCount === 0) return "Nothing scheduled yet.";
  const summary = `${sessionsCount} ${sessionsCount === 1 ? "session" : "sessions"} across ${classesCount} ${classesCount === 1 ? "class" : "classes"}.`;
  return shown < sessionsCount ? `${summary} Showing the next ${shown}.` : summary;
}

function SkillTags({ skills }) {
  if (!skills || skills.length === 0) return null;
  return (
    <span className="instructor-skill-tags">
      {skills.map((skill) => <Tag key={skill} size="sm">{skill}</Tag>)}
    </span>
  );
}

SkillTags.propTypes = { skills: PropTypes.arrayOf(PropTypes.string) };

function UpcomingSessionsCard({ sessions, sessionsCount, classesCount, classesPath }) {
  const columns = [
    { key: "class", header: "Class", primary: true, render: (session) => session.className },
    { key: "session", header: "Session", render: (session) => session.name || "—" },
    {
      key: "when",
      header: "When",
      width: 150,
      render: (session) => (
        <CellText primary={sessionDay(session.startsAt, session.timeZone)} supporting={sessionTimeRange(session.startsAt, session.endsAt, session.timeZone)} />
      ),
    },
    {
      key: "open",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      render: (session) => (
        <Button
          as="a"
          variant="tertiary"
          size="sm"
          icon={ChevronRight}
          href={`${classesPath}/${session.classInstanceId}?tab=sessions`}
          aria-label={`Open ${session.className}`}
        />
      ),
    },
  ];

  return (
    <Card padding="none" header={<CardHeader title="Upcoming Sessions" supportingText={upcomingSummary(sessionsCount, classesCount, sessions.length)} />}>
      <Table className="classes-table" columns={columns} data={sessions} getRowKey={(session) => session.id} stackBelow={400} empty="No upcoming sessions." />
    </Card>
  );
}

UpcomingSessionsCard.propTypes = {
  sessions: PropTypes.array.isRequired,
  sessionsCount: PropTypes.number.isRequired,
  classesCount: PropTypes.number.isRequired,
  classesPath: PropTypes.string.isRequired,
};

function ProfileCard({ instructor }) {
  return (
    <Card header={<CardHeader title="Profile" />}>
      <dl className="row mb-0">
        <DetailField label="Email" wide>{instructor.email}</DetailField>
        <DetailField label="Phone" wide>{instructor.human_phone || instructor.phone}</DetailField>
        <DetailField label="Skills" wide><SkillTags skills={instructor.skills} /></DetailField>
        <DetailField label="Website" wide>
          {instructor.website_url && (
            <a href={instructor.website_url} target="_blank" rel="noopener noreferrer" className="instructor-link">
              {instructor.website_url.replace(/^https?:\/\//, "")}
            </a>
          )}
        </DetailField>
        {instructor.instagram_handle && <DetailField label="Instagram" wide>{instructor.instagram_handle}</DetailField>}
      </dl>
    </Card>
  );
}

ProfileCard.propTypes = { instructor: PropTypes.object.isRequired };

function InstructorShowApp(props) {
  const {
    instructor, canManage, editPath, archivePath, unarchivePath, csrfToken, breadcrumb,
    upcomingSessions, upcomingSessionsCount, upcomingClassesCount, allTimeSessionsCount, classesPath,
  } = props;
  const [working, setWorking] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  const toggleArchive = async () => {
    setWorking(true);
    try {
      await axios.post(instructor.archived ? unarchivePath : archivePath, {}, {
        headers: { "X-CSRF-Token": csrfToken },
      });
      window.location.reload();
    } catch (e) {
      setWorking(false);
      toastError("Could not update instructor.");
    }
  };

  const actions = canManage && (
    <div className="d-flex align-items-center classes-gap-8">
      <Button as="a" variant="secondary" icon={Edit01} href={editPath}>Edit Profile</Button>
      <Menu
        placement="bottom-end"
        trigger={<Button variant="tertiary" icon={DotsVertical} aria-label="More instructor actions" disabled={working} />}
        items={[{ label: instructor.archived ? "Unarchive Instructor" : "Archive Instructor", icon: Archive, onClick: () => (instructor.archived ? toggleArchive() : setConfirmingArchive(true)) }]}
      />
    </div>
  );

  return (
    <DetailPageLayout
      breadcrumbs={breadcrumb}
      avatar={<Avatar size="2xl" initials={initialsFor(instructor.name)} {...personColorProps(instructor.name)} src={instructor.photo_url || undefined} alt={instructor.name} />}
      title={instructor.name}
      sectionTitle="Instructor details"
      badges={instructor.archived && <Badge color="gray" size="md" dot>Archived</Badge>}
      supportingText={(
        <>
          <span>{instructorMeta(instructor, allTimeSessionsCount)}</span>
          {instructor.skills && instructor.skills.length > 0 && (
            <span className="d-block mt-2"><SkillTags skills={instructor.skills} /></span>
          )}
        </>
      )}
      actions={actions}
    >
      <DetailColumns rail={<ProfileCard instructor={instructor} />}>
        <Card header={<CardHeader title="Bio" />}>
          {instructor.bio
            ? <div className="instructor-bio">{instructor.bio}</div>
            : <div className="classes-text-secondary">No bio yet.</div>}
        </Card>
        <UpcomingSessionsCard
          sessions={upcomingSessions}
          sessionsCount={upcomingSessionsCount}
          classesCount={upcomingClassesCount}
          classesPath={classesPath}
        />
      </DetailColumns>
      {confirmingArchive && (
        <ArchiveConfirmModal
          kind="instructor"
          name={instructor.name}
          working={working}
          onConfirm={toggleArchive}
          onClose={() => setConfirmingArchive(false)}
        />
      )}
      <ToastContainer icon={false} />
    </DetailPageLayout>
  );
}

InstructorShowApp.propTypes = {
  instructor: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    title: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    human_phone: PropTypes.string,
    website_url: PropTypes.string,
    instagram_handle: PropTypes.string,
    bio: PropTypes.string,
    skills: PropTypes.arrayOf(PropTypes.string),
    photo_url: PropTypes.string,
    archived: PropTypes.bool,
    created_at: PropTypes.string,
  }).isRequired,
  canManage: PropTypes.bool,
  editPath: PropTypes.string,
  indexPath: PropTypes.string,
  archivePath: PropTypes.string,
  unarchivePath: PropTypes.string,
  csrfToken: PropTypes.string,
  breadcrumb: PropTypes.array,
  upcomingSessions: PropTypes.array,
  upcomingSessionsCount: PropTypes.number.isRequired,
  upcomingClassesCount: PropTypes.number.isRequired,
  allTimeSessionsCount: PropTypes.number.isRequired,
  classesPath: PropTypes.string,
};

InstructorShowApp.defaultProps = {
  upcomingSessions: [],
  classesPath: "",
};

export default InstructorShowApp;
